import { FunctionComponent, useEffect, useState } from 'react';
import { InputDateRange } from '../DatePicker/InputDateRange';
import { Position } from '../DatePicker/InputDateRange/types';
import { Timestamp } from '../DatePicker/types';
import InvalidEndDatePopup from '../DatePicker/datepicker-popup/invalid-enddate-popup/invalid-enddate-popup';
import styles from './DateRangePicker.module.scss';
import { DatePickerSimpleProps } from '../DatePicker/DatePickerSimple';

export type DateRangeProps = {
  triggerVariant?: DatePickerSimpleProps['variant'];
  position?: Position;
  initialTime?: [Timestamp, Timestamp];
  // To use controlled input value, the `value` prop needs to be defined. Use `null` to specify that the calendar picker should be empty.
  value?: [Timestamp, Timestamp] | null;
  placeholder?: string;
  disabled?: boolean;
  hasInvalidEndDate?: boolean;
  allowBlank?: boolean;
  shouldShowEndDateError?: boolean;
  startYearOffset?: number;
  setShouldShowEndDateError?: (event: boolean) => void;
  onChange: (nextValue: [Timestamp, Timestamp]) => void;
};

type FC = FunctionComponent<DateRangeProps>;

const DateRange: FC = ({
  triggerVariant,
  position,
  initialTime,
  disabled,
  hasInvalidEndDate,
  shouldShowEndDateError,
  allowBlank,
  value,
  startYearOffset,
  setShouldShowEndDateError,
  onChange,
}: DateRangeProps) => {
  const [internalValue, setInternalValue] = useState<[Timestamp, Timestamp] | undefined>(initialTime);

  const handleChange = (nextValue: [Timestamp, Timestamp]) => {
    setInternalValue(nextValue);
    if (nextValue) {
      onChange(nextValue);
    }
  };

  useEffect(() => {
    if (value === undefined) return;
    if (internalValue && value && (internalValue[0] !== value[0] || internalValue[1] !== value[1])) {
      setInternalValue(value);
    } else if (!internalValue && value) {
      setInternalValue(value);
    } else if (internalValue && !value) {
      setInternalValue(undefined);
    }
  }, [value]);

  function getDisabledClass() {
    return disabled ? styles.disabled : '';
  }

  return (
    <div className={styles[getDisabledClass()]}>
      <InputDateRange
        triggerVariant={triggerVariant}
        position={position}
        allowBlank={allowBlank}
        value={internalValue}
        startYearOffset={startYearOffset}
        onChange={handleChange}
        hasInvalidEndDate={hasInvalidEndDate}
        disabled={disabled}
      />
      <InvalidEndDatePopup
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
      />
    </div>
  );
};

export default DateRange;
